import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type {
  OccasionTopicMessage,
  RsvpEdge
} from '@aurora/shared-generated/types/graphql-schema-types';
import { RsvpResponse } from '@aurora/shared-generated/types/graphql-schema-types';
import React, { useContext, useEffect, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { OccasionRsvpsQuery, OccasionRsvpsQueryVariables } from '../../../types/graphql-types';
import occasionRsvpsQuery from '../OccasionRsvpIndicator/OccasionRsvps.query.graphql';
import OccasionRsvpStatusForCurrentUser from '../OccasionRsvpStatusForCurrentUser/OccasionRsvpStatusForCurrentUser';
import localStyles from './OccasionRsvpInformation.module.pcss';

interface Props {
  /**
   * The message for which rsvp has to be displayed in the published view.
   */
  occasion: OccasionTopicMessage;
  /**
   * The className to be applied to the component.
   */
  className?: string;
}

/**
 * Renders rsvp information on an Occasion published view page.
 *
 * @author Vishnu Das
 */
const OccasionRsvpInformation: React.FC<React.PropsWithChildren<Props>> = ({
  occasion,
  className
}) => {
  const cx = useClassNameMapper(localStyles);
  const { authUser } = useContext(AppContext);
  const [rsvpStatus, setRsvpStatus] = useState<RsvpResponse>(RsvpResponse.No);
  const { data: authUserOccasionRsvpData, loading: occasionRsvpLoading } = useQueryWithTracing<
    OccasionRsvpsQuery,
    OccasionRsvpsQueryVariables
  >(module, occasionRsvpsQuery, {
    variables: {
      constraints: {
        occasionId: { eq: occasion?.id },
        userId: { eq: authUser?.id }
      }
    }
  });

  useEffect(() => {
    if (!occasionRsvpLoading) {
      const rsvpStatusForAuthUser = authUserOccasionRsvpData?.rsvps?.edges[0] as RsvpEdge;
      const defaultRsvpStatus = rsvpStatusForAuthUser
        ? rsvpStatusForAuthUser?.node?.rsvpResponse
        : RsvpResponse.No;
      if (defaultRsvpStatus !== RsvpResponse.No) {
        setRsvpStatus(defaultRsvpStatus);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occasionRsvpLoading]);

  if (occasionRsvpLoading) {
    return null;
  }

  return (
    <OccasionRsvpStatusForCurrentUser
      occasionId={occasion?.id}
      rsvpStatus={rsvpStatus}
      onRsvpChange={rsvpResponse => setRsvpStatus(rsvpResponse)}
      className={cx('lia-occasion-rsvp', className)}
    />
  );
};

export default OccasionRsvpInformation;
